import React, { createContext, useEffect, useRef, useState } from 'react';
import { Provider } from 'react-redux';

import configureStore from '../store/webOrderFormStore';
import WebOrderFormContainer from '../containers/WebOrderFormContainer';
import {
  widgetConfigReplaced,
  checkoutCategoriesReplaced,
  ticketTypesChanged,
  addOnsChanged,
} from '../actions/webOrderFormActionCreators';
import ActionCable from "actioncable";

const CableApp = {};
CableApp.cable = ActionCable.createConsumer('/cable');
const ActionCableContext = createContext();

// See documentation for https://github.com/reactjs/react-redux.
// This is how you get props from the Rails view into the redux store.
//
// The store is created once per mount via useState so that re-renders
// (e.g. when this component is embedded in the admin builder and the
// surrounding tree re-renders for unrelated reasons) don't blow away
// Redux state that was hydrated by data-loading useEffects in children.
//
// When the parent (the inline preview in the custom widget builder) hands
// us new widgetConfig or checkoutCategories props, we dispatch replace
// actions instead of remounting the whole tree — this keeps cart-shaped
// state (ticketReservation, addOns quantities, ticketTypes already
// fetched, typed promo codes, etc.) intact across builder edits.
const WebOrderFormApp = (props) => {
  const [store] = useState(() => configureStore(props));
  const isFirstWidgetConfig = useRef(true);
  const isFirstCheckoutCategories = useRef(true);
  const isFirstTicketTypes = useRef(true);
  const isFirstAddOns = useRef(true);

  useEffect(() => {
    if (isFirstWidgetConfig.current) {
      isFirstWidgetConfig.current = false;
      return;
    }
    store.dispatch(widgetConfigReplaced(props.widgetConfig));
  }, [props.widgetConfig]);

  useEffect(() => {
    if (isFirstCheckoutCategories.current) {
      isFirstCheckoutCategories.current = false;
      return;
    }
    store.dispatch(checkoutCategoriesReplaced(props.checkoutCategories));
  }, [props.checkoutCategories]);

  useEffect(() => {
    if (isFirstTicketTypes.current) {
      isFirstTicketTypes.current = false;
      return;
    }
    if (props.ticketTypes) store.dispatch(ticketTypesChanged(props.ticketTypes));
  }, [props.ticketTypes]);

  useEffect(() => {
    if (isFirstAddOns.current) {
      isFirstAddOns.current = false;
      return;
    }
    if (props.addOns) store.dispatch(addOnsChanged(props.addOns));
  }, [props.addOns]);

  useEffect(() => {
    if (typeof props.onTicketReservationChange !== 'function') return undefined;

    let last = null;
    const publish = () => {
      const state = store.getState();
      const token = state.ticketReservation?.token || null;
      const placingOrder = Boolean(state.isPlacingOrder);
      if (last && token === last.token && placingOrder === last.placingOrder) return;
      last = { token, placingOrder };
      props.onTicketReservationChange(token, placingOrder);
    };

    publish();
    return store.subscribe(publish);
  }, [store, props.onTicketReservationChange]);

  return (
    <Provider store={store}>
      <ActionCableContext.Provider value={CableApp}>
        <WebOrderFormContainer onRestart={props.onRestart} />
      </ActionCableContext.Provider>
    </Provider>
  );
};

export default WebOrderFormApp;
export { ActionCableContext };
